import {
  Activity,
  CalendarClock,
  Cpu,
  HardDrive,
  KeyRound,
  LayoutDashboard,
  Mail,
  Plug,
  ScrollText,
  Server,
  TrendingUp,
  UsersRound,
  Wallet,
  type LucideIcon,
} from "../../components/icons.js";

/**
 * Every page of the operator panel, once.
 *
 * The router builds its routes from `path`, the rail draws its links from the
 * groups, the top bar and the tab title read `label`, and each page's head reads
 * `question`, so the four cannot drift apart (the customer app's `PATHS` and
 * `SCREEN_NAMES` are the same idea). A page's `question` is what it exists to
 * answer, in the words an operator would use (DESIGN.md §14).
 */
export const ADMIN_GROUPS = [
  "Today",
  "Money",
  "Capture",
  "Customers",
  "Platform",
] as const;
export type AdminGroup = (typeof ADMIN_GROUPS)[number];

export type AdminPageKey =
  | "overview"
  | "spend"
  | "model"
  | "capture"
  | "engines"
  | "jobs"
  | "customers"
  | "accounts"
  | "usage"
  | "emails"
  | "services"
  | "storage"
  | "audit";

export interface AdminPageDef {
  readonly key: AdminPageKey;
  readonly path: string;
  readonly label: string;
  readonly question: string;
  readonly icon: LucideIcon;
  readonly group: AdminGroup;
}

export const ADMIN_ROOT = "/admin";

export const ADMIN_PAGES: readonly AdminPageDef[] = [
  {
    key: "overview",
    path: ADMIN_ROOT,
    label: "Overview",
    question: "Does anything need a look right now?",
    icon: LayoutDashboard,
    group: "Today",
  },
  {
    key: "spend",
    path: `${ADMIN_ROOT}/spend`,
    label: "Spend",
    question: "Are we within budget this month?",
    icon: Wallet,
    group: "Money",
  },
  {
    key: "model",
    path: `${ADMIN_ROOT}/model`,
    label: "Model usage",
    question: "Is the model keeping up, and how often does it fail?",
    icon: Cpu,
    group: "Money",
  },
  {
    key: "capture",
    path: `${ADMIN_ROOT}/capture`,
    label: "Capture health",
    question: "Did today's answers arrive?",
    icon: Activity,
    group: "Capture",
  },
  {
    key: "engines",
    path: `${ADMIN_ROOT}/engines`,
    label: "Engines",
    question: "Where does each answer come from, and what does it cost?",
    icon: Plug,
    group: "Capture",
  },
  {
    key: "jobs",
    path: `${ADMIN_ROOT}/jobs`,
    label: "Jobs",
    question: "Are the background jobs running on time?",
    icon: CalendarClock,
    group: "Capture",
  },
  {
    key: "customers",
    path: `${ADMIN_ROOT}/customers`,
    label: "Customers",
    question: "Who are our customers, and who is stuck?",
    icon: UsersRound,
    group: "Customers",
  },
  {
    key: "accounts",
    path: `${ADMIN_ROOT}/accounts`,
    label: "Accounts",
    question: "Who has access, and until when?",
    icon: KeyRound,
    group: "Customers",
  },
  {
    key: "usage",
    path: `${ADMIN_ROOT}/usage`,
    label: "Usage",
    question: "Do people use Crux, and come back?",
    icon: TrendingUp,
    group: "Customers",
  },
  {
    key: "emails",
    path: `${ADMIN_ROOT}/emails`,
    label: "Emails",
    question: "Is our mail going out, and inside the daily cap?",
    icon: Mail,
    group: "Customers",
  },
  {
    key: "services",
    path: `${ADMIN_ROOT}/services`,
    label: "Services",
    question: "Is everything Crux stands on up?",
    icon: Server,
    group: "Platform",
  },
  {
    key: "storage",
    path: `${ADMIN_ROOT}/storage`,
    label: "Storage",
    question: "How much do we keep, and how long until the disk is full?",
    icon: HardDrive,
    group: "Platform",
  },
  {
    key: "audit",
    path: `${ADMIN_ROOT}/audit`,
    label: "Audit log",
    question: "What did we change, and who did it?",
    icon: ScrollText,
    group: "Platform",
  },
];

/**
 * The pages whose numbers are real. Any other page wears the "Sample data"
 * badge. Here, not beside the sample source, so reading it does not bring the
 * sample data along.
 */
export const LIVE_PAGES: ReadonlySet<AdminPageKey> = new Set([
  "overview",
  "spend",
  "model",
  "capture",
  "engines",
  "jobs",
  "customers",
  "usage",
  "emails",
  "storage",
  "services",
  "audit",
  "accounts",
]);

export function adminPage(key: AdminPageKey): AdminPageDef {
  const page = ADMIN_PAGES.find((p) => p.key === key);
  if (page === undefined) throw new Error(`No admin page ${key}`);
  return page;
}

/** The page an address belongs to; the overview owns `/admin` and nothing under it. */
export function pageForPath(pathname: string): AdminPageDef | undefined {
  const clean = pathname.replace(/\/$/u, "");
  return ADMIN_PAGES.find((p) => p.path === clean);
}
